import Image from '@components/atoms/Image';
import { CartLine } from '@interfaces/cart';
import React from 'react';
import { useCartContext } from './CartContext';
import CartItemPrice from './CartItemPrice';
import { handlelize } from '@helpers/global';
import Icon from '@components/atoms/Icon';
import { generateTrackingItem, trackCartEvent } from '@helpers/trackCartActions';

const CartItem: React.FC<{ line: CartLine }> = ({ line }) => {
  const { data: { isCartPage }, cart } = useCartContext();
  const maxQuantity = line.merchandise.quantityAvailable;

  const handleRemove = () => {
    // Generate tracking item and track event
    const removedItem = generateTrackingItem(line);
    trackCartEvent('remove_from_cart', { items: [removedItem] });
  
    // Remove the item from the cart
    cart.remove([line.id]);
  };

  const handleAdjustQuantity = (newQuantity: number) => {
    const adjustedQuantity = Math.min(newQuantity, maxQuantity); // Ensure quantity is not above max
  
    if (adjustedQuantity < 1) {
      // If the adjusted quantity is 0, remove the item
      handleRemove();
    } else if (adjustedQuantity < line.quantity) {
      // If quantity decreases, track the removed portion
      const removedQuantity = line.quantity - adjustedQuantity;
      const removedItem = generateTrackingItem(line, removedQuantity);
      trackCartEvent('remove_from_cart', { items: [removedItem] });
  
      // Update the cart with the new quantity
      cart.update([{ id: line.id, quantity: adjustedQuantity }]);
    } else if (adjustedQuantity > line.quantity) {
      // If quantity increases, track the added portion
      const addedQuantity = adjustedQuantity - line.quantity;
      const addedItem = generateTrackingItem(line, addedQuantity);
      trackCartEvent('add_to_cart', { items: [addedItem] });
  
      // Update the cart with the new quantity
      cart.update([{ id: line.id, quantity: adjustedQuantity }]);
    } else {
      // If the quantity remains the same, just update the cart
      cart.update([{ id: line.id, quantity: adjustedQuantity }]);
    }
  };

  const processSelectedOptions = (options: Array<{ name: string; value: string }>) => {
    return options.filter(option => option.value.toLowerCase() !== 'default title');
  };

  const lineItem = line.merchandise;

  return (
    <div className={`flex items-center gap-7 bg-grey-5 px-2 py-1 rounded-sm mb-4 ${isCartPage ? 'md:gap-8 md:px-4 md:pt-4' : ''}`} key={line.id}>
      <a className={`block max-w-30 ${isCartPage ? 'md:max-w-55.5' : ''}`} href={`/products/${lineItem.product.handle}`}>
        <Image
          alt={lineItem.image.altText || lineItem.product.title}
          src={lineItem.image.url}
          srcWidths={{ sm: 640 }}
          height={lineItem.image.height}
          width={lineItem.image.width}
          wrapperClasses="bg-white rounded shadow-md flex items-center justify-center"
          classes={`aspect-[120/117] object-contain bg-white rounded max-w-30 ${isCartPage ? 'md:max-w-55.5' : ''}`}
          blendMode="multiply"
        />
      </a>
      <div className={`flex-1 h-full flex flex-col justify-between self-start gap-4 ${isCartPage ? 'md:py-2' : ''}`}>
        <div className={`flex flex-col gap-4 ${isCartPage ? 'md:flex-row md:justify-between' : ''}`}>
          <div className="flex flex-col">
            <h3 className="body-sm">{lineItem.product.title}</h3>
            <p className="body-xxs capitalize flex flex-nowrap" key={`combined-${lineItem.id}`}>
              {processSelectedOptions(lineItem.selectedOptions)
                .map((option, index) =>
                  <span key={`${index}-${handlelize(option.name)}-${handlelize(option.value)}`}>
                    {index > 0 ? ' / ' : ''}{option.value}
                  </span>
                )}
            </p>
            {lineItem.discounts && lineItem.discounts.length > 1 && (
              <p className="body-xxs capitalize mt-3 text-red-700">{lineItem.discounts.length} Discounts Applied</p>
            )}
            {lineItem.discounts && lineItem.discounts.length === 1 && (
              <p className="body-xxs capitalize mt-3 text-red-700">{lineItem.discounts[0].title === 'GWP' ? 'Free Gift' : lineItem.discounts[0].title}</p>
            )}
          </div>
          <CartItemPrice
            cost={line.cost}
            isCartPage={isCartPage}
            isGwp={line.attributes.some(attr => attr.key === 'type' && attr.value === 'gwp')}
          />
        </div>
        <div className={`self-auto flex items-center flex-wrap gap-3 justify-between ${isCartPage ? 'sm:self-end md:gap-9' : ''}`}>
          <button
            type="button"
            className="body-xs text-black underline"
            onClick={handleRemove}
          >
            {line.quantity > 1
              ? window.DEPT.Strings.cart.removeAll
              : window.DEPT.Strings.cart.remove
            }
          </button>
          <div className="flex items-center gap-5">
            <button
              className="border rounded-full border-blue-1 cursor-pointer w-6 h-6 flex items-center justify-center bg-gradient-model-grey shadow-thumbnail"
              onClick={() => handleAdjustQuantity(line.quantity - 1)}
              aria-label={line.quantity === 1 ? window.DEPT.Strings.cart.removeAll : window.DEPT.Strings.cart.remove}
            >
              {line.quantity === 1 ? (
                <Icon name="trash" className="text-black" />
              ) : (
                <Icon name="minus" className="text-black w-2.5" strokeWidth={2} />
              )}
            </button>
            <div className="body-md text-black">
              {line.quantity}
            </div>
            <button
              className="border rounded-full border-blue-1 cursor-pointer w-6 h-6 flex items-center justify-center bg-gradient-model-grey shadow-thumbnail disabled:opacity-10"
              onClick={() => handleAdjustQuantity(line.quantity + 1)}
              aria-label={window.DEPT.Strings.cart.add}
              disabled={line.attributes.some(attr => attr.key === 'type' && attr.value === 'gwp')}
            >
              <Icon name="plus" className="text-black w-2.5" strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
